import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import Filter from '@/src/components/organism/Filter';
import { partnershipLinkFilters } from './filter';
import SectionCardNoneTitle from '@/src/components/molecule/SectionCardNoneTitle';
import Typo from '@/src/components/atom/Typo';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Table from '@/src/components/atom/Table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { partnershipLinkListColumn } from './column';
import { Link, useNavigate } from 'react-router-dom';
import PATH from '@/src/routes/path';
import { useQuery } from '@tanstack/react-query';
import { getPartnershipLinksAPI } from '@/src/apis/rest/partnershipCompanies';
import usePaginationQuery from '@/src/hooks/usePaginationQuery';
import useQueryValues from '@/src/hooks/useQueryValues';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';

const PartnershipLinksListPage = () => {
  const navigate = useNavigate();

  const { defaultValues } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
    postedAt: ['startedAtFrom', 'startedAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();

  const gridRef = useRef<AgGridReact>(null);
  const [values, setValues] = useState(defaultValues);

  const {
    data: partnershipLinksListData,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['partnership-links', values, page, pageSize],
    queryFn: () => {
      return getPartnershipLinksAPI({
        ...values,
        page,
        pageSize,
      });
    },
  });

  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  const handleUrlClick = useCallback(async (urlCode: string) => {
    const isProd = process.env.REACT_APP_ENV_MODE === 'prod';

    try {
      await navigator.clipboard.writeText(
        isProd
          ? `https://partners.ecoyaearth.com/${urlCode}`
          : `https://dev-partners.ecoyaearth.com/${urlCode}`,
      );
      toast({
        variant: 'success',
        title: TOAST_CONST.saveSuccess.title,
        description: '클립보드에 복사되었습니다',
      });
    } catch (e) {
      toast({
        variant: 'error',
        title: TOAST_CONST.error.title,
        description: '클립보드 복사에 실패했습니다',
      });
    }
  }, []);

  return (
    <>
      <MainContentTitle
        pageTitle="제휴사 링크 관리"
        breadcrumbList={['제휴사 링크 관리']}
      />

      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={partnershipLinkFilters()}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setValues(values);
          }}
        />

        <div>
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">외부 링크 목록</Typo>

              <Link to={PATH.PARTNERSHIP_LINKS_CREATE}>
                <Button buttonSize="medium" onClick={() => {}}>
                  <div className="flex items-center gap-x-2">
                    <LucideIcon name="Plus" color="white" />
                    제휴사 링크 등록
                  </div>
                </Button>
              </Link>
            </div>

            <Table
              ref={gridRef}
              rowData={isError ? [] : partnershipLinksListData?.rows || []}
              isPaginationDataMapping
              columnDefs={partnershipLinkListColumn({
                handleUrlClick: handleUrlClick,
                onDetailClick: (id: number) => {
                  navigate(
                    PATH.PARTNERSHIP_LINKS_DETAIL.replace(':id', String(id)),
                  );
                },
              })}
              total={partnershipLinksListData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>
    </>
  );
};

export default PartnershipLinksListPage;
